// src/components/BillTwoSegmentRouter.tsx
import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { isValidCountrySlug, isValidStateSlug } from '../utils/stateUtils';
import StateBillsPage from '../pages/StateBillsPage';

/**
 * Smart router component that resolves 2-segment ambiguity for /bill/:seg1/:seg2
 *
 * Disambiguation logic (per contracts/url-routing.md):
 *
 * 1. If seg1 is a valid country slug (e.g., "united-states", "canada"):
 *    → Render StateBillsPage (country=seg1, state=seg2)
 *
 * 2. If seg1 is a valid state slug (e.g., "texas"):
 *    → Legacy URL — redirect to /bill/united-states/:seg1/:seg2
 *
 * 3. Otherwise:
 *    → Not found / fallback to home
 *
 * Examples:
 *   /bill/united-states/texas  → StateBillsPage (US/Texas)
 *   /bill/canada/ontario       → StateBillsPage (Canada/Ontario)
 *   /bill/texas/three-strikes  → Navigate → /bill/united-states/texas/three-strikes
 *   /bill/invalid/anything     → Navigate → /
 */
const BillTwoSegmentRouter: React.FC = () => {
  const { seg1, seg2 } = useParams<{ seg1: string; seg2: string }>();

  if (!seg1 || !seg2) {
    return <Navigate to="/" replace />;
  }

  // Case 1: seg1 is a valid country slug → render StateBillsPage
  if (isValidCountrySlug(seg1)) {
    return <StateBillsPage />;
  }

  // Case 2: seg1 is a valid state slug → legacy URL, redirect with united-states prefix
  if (isValidStateSlug(seg1)) {
    return <Navigate to={`/bill/united-states/${seg1}/${seg2}`} replace />;
  }

  // Case 3: neither country nor state → not found
  return <Navigate to="/" replace />;
};

export default BillTwoSegmentRouter;
